import { useCallback, useEffect, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  daysSince,
  daysAgoText,
  deviceLabel,
  invoicesOwing,
  pilotSeatWords,
  releaseWords,
  seatDeviceUse,
  seatUsage,
  splitPilotSeats,
  subscriptionBanner,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { ErrorState, Loading, PageHeader } from '../components/States.tsx';
import { dateText, moneyText, type Invoice, type Licence } from '../admin/api.ts';
import { INVOICE_STATUS } from '../admin/AdminTenantDetail.tsx';
import { SyncVersionChip, belowMinimum, belowRecommended } from '../admin/AdminDevicesScreen.tsx';
import { loadLicencesPage, release, type LicenceSeat, type LicencesPage } from './api.ts';

const message = (e: unknown): string => (e instanceof Error ? e.message : String(e));
type Notice = { readonly ok: boolean; readonly text: string } | null;

export interface LicencesScreenProps {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  readonly tenantName: string;
  /** Shown under the sign-in refusal (no free seat, licence ended): no page title, and a change is reported up. */
  readonly embedded?: boolean;
  readonly onReleased?: () => void;
  /** The Invoices panel; shown on the page itself, and under a refusal only when asked (licence ended). */
  readonly showInvoices?: boolean;
  /** The Seats and devices panel; left out under the refusal of an ended licence (decision 865: licence and invoices). */
  readonly showSeats?: boolean;
  readonly now?: () => Date;
}

/**
 * Company → Licences (decisions 295, 447, 838): the organisation's licence, its seats with each holder's laptops and
 * phones (Teslyn Field version, days since each checked in), releasing a seat or a device, and Braynewave's invoices.
 * Tenant admins only; the database refuses everyone else and records every release.
 */
export function LicencesScreen({
  sb,
  tenantId,
  tenantName,
  embedded = false,
  onReleased,
  showInvoices = !embedded,
  showSeats = true,
  now = () => new Date(),
}: LicencesScreenProps): ReactElement {
  const [page, setPage] = useState<LicencesPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<Notice>(null);

  const reload = useCallback(() => {
    setError(null);
    loadLicencesPage(sb, tenantId)
      .then(setPage)
      .catch((e: unknown) => setError(message(e)));
  }, [sb, tenantId]);
  useEffect(reload, [reload]);

  const onRelease = async (
    what: { readonly seat: string } | { readonly device: string },
    note: string,
    done: string,
  ): Promise<void> => {
    try {
      await release(sb, what, note);
      setNotice({ ok: true, text: done });
      onReleased?.();
    } catch (e) {
      setNotice({ ok: false, text: message(e) });
    }
    reload();
  };

  const at = now();
  return (
    <section className={embedded ? 'stack' : 'page'}>
      {!embedded && (
        <PageHeader
          title="Licences"
          subtitle={`${tenantName}’s Teslyn licence: who holds a seat, on which laptops and phones, and Braynewave’s invoices.`}
        />
      )}
      {notice && (
        <p className={notice.ok ? 'notice' : 'notice notice--fail'} role={notice.ok ? 'status' : 'alert'}>
          {notice.text}
        </p>
      )}
      {error && <ErrorState message={error} onRetry={reload} />}
      {!page && !error && <Loading what="your licence" />}
      {page && (
        <>
          <LicencePanel page={page} tenantName={tenantName} now={at} />
          {showSeats && <SeatsPanel page={page} now={at} onRelease={onRelease} />}
          {showInvoices && <InvoicesPanel invoices={page.invoices} />}
        </>
      )}
    </section>
  );
}

function LicencePanel({
  page,
  tenantName,
  now,
}: {
  readonly page: LicencesPage;
  readonly tenantName: string;
  readonly now: Date;
}): ReactElement {
  const l = page.licence;
  return (
    <div className="panel stack">
      <h2 className="heading">Your licence</h2>
      {!l ? (
        <p className="body">
          Braynewave has not set up a licence for {tenantName} yet. Ask Braynewave to record your plan and
          seats.
        </p>
      ) : (
        <LicenceFacts
          licence={l}
          seatsInUse={splitPilotSeats(page.seats, page.pilotOnly).staff.length}
          now={now}
        />
      )}
      <p className="caption muted" data-testid="web-check">
        {page.webCheck
          ? 'The seat check at sign-in is on: each staff member takes a seat the first time they sign in to Teslyn Cloud or Teslyn Field, and keeps it until it is released. Client accounts never need one.'
          : 'The seat check at sign-in is switched off until Teslyn goes live: staff sign in to Teslyn Cloud without taking a seat. Seats show here once laptops or sign-ins take them.'}
      </p>
    </div>
  );
}

function LicenceFacts({
  licence: l,
  seatsInUse,
  now,
}: {
  readonly licence: Licence;
  readonly seatsInUse: number;
  readonly now: Date;
}): ReactElement {
  const usage = seatUsage(l.seats, seatsInUse);
  const banner = subscriptionBanner(l.subscriptionEnds, now);
  return (
    <>
      <dl className="kv">
        <dt>Seats</dt>
        <dd>
          {usage.words}{' '}
          {usage.over > 0 || usage.free === 0 ? (
            <span className="chip chip--warn label">{usage.over > 0 ? 'Over the licence' : 'Full'}</span>
          ) : null}
        </dd>
        <dt>Each seat covers</dt>
        <dd>
          {l.laptopsPerUser === 1 ? '1 laptop' : `${l.laptopsPerUser} laptops`} with Teslyn Field,{' '}
          {l.mobilesPerUser === 1 ? '1 phone' : `${l.mobilesPerUser} phones`} with Teslyn Mobile, and Teslyn
          Cloud in any browser. Pilot sign-ins take no seat
        </dd>
        <dt>Plan</dt>
        <dd className="data">{l.plan}</dd>
        <dt>Status</dt>
        <dd>
          {l.status === 'active' ? (
            <span className="chip chip--pass label">Active</span>
          ) : (
            <span className="chip chip--fail label">Cancelled</span>
          )}
        </dd>
        <dt>Subscription ends</dt>
        <dd>
          {l.subscriptionEnds ? dateText(l.subscriptionEnds) : 'No end date recorded'}{' '}
          {banner && (
            <span className={`chip label ${banner.level === 'fail' ? 'chip--fail' : 'chip--warn'}`}>
              {banner.level === 'fail'
                ? 'Ended'
                : `${banner.days === 1 ? '1 day' : `${banner.days} days`} left`}
            </span>
          )}
        </dd>
        {l.notes && (
          <>
            <dt>Notes from Braynewave</dt>
            <dd>{l.notes}</dd>
          </>
        )}
      </dl>
      <p className="caption muted">
        Only Braynewave changes the plan, the number of seats and what a seat covers. Ask Braynewave for more
        seats.
      </p>
    </>
  );
}

type Ending = {
  readonly what: { readonly seat: string } | { readonly device: string };
  readonly words: string;
  readonly done: string;
};

function SeatsPanel({
  page,
  now,
  onRelease,
}: {
  readonly page: LicencesPage;
  readonly now: Date;
  readonly onRelease: (what: Ending['what'], note: string, done: string) => Promise<void>;
}): ReactElement {
  const [ending, setEnding] = useState<Ending | null>(null);
  const [note, setNote] = useState('');
  const limits = page.licence ?? { laptopsPerUser: 1, mobilesPerUser: 2 };
  const ask = (e: Ending): void => {
    setNote('');
    setEnding(e);
  };
  const days = (iso: string | null): string => daysAgoText(daysSince(iso, now));
  const { staff, pilots } = splitPilotSeats(page.seats, page.pilotOnly);
  const pilotPhones = pilots.reduce((n, s) => n + s.devices.filter((d) => d.kind === 'mobile').length, 0);
  const table = (rows: readonly LicenceSeat[]): ReactElement => (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th className="label">Person</th>
            <th className="label">Last check-in</th>
            <th className="label">Laptops and phones</th>
            <th className="label">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => (
            <tr key={s.id}>
              <td>
                {s.name}
                {s.email && (
                  <>
                    <br />
                    <span className="caption muted data">{s.email}</span>
                  </>
                )}
                <br />
                <span className="caption muted">Seat since {dateText(s.takenAt)}</span>
              </td>
              <td className="caption">
                {days(s.lastCheckinAt)}
                {s.lastWebAt && (
                  <>
                    <br />
                    <span className="muted">Teslyn Cloud {days(s.lastWebAt)}</span>
                  </>
                )}
              </td>
              <td>
                <p className="caption muted">{seatDeviceUse(s.devices, limits).words}</p>
                {s.devices.length === 0 ? (
                  <span className="muted">Teslyn Cloud only</span>
                ) : (
                  <ul className="stack">
                    {s.devices.map((d) => {
                      const name = d.label || '(no name)';
                      const kind = deviceLabel(d.kind, d.platform);
                      return (
                        <li key={d.id} className="row">
                          <span className="body">
                            {kind} <span className="data">{name}</span>{' '}
                            <span className="caption muted">· {days(d.lastCheckinAt)}</span>{' '}
                            <span className="caption muted data">
                              ·{' '}
                              {d.appVersion
                                ? `${d.kind === 'laptop' ? 'Teslyn Field ' : ''}${d.appVersion}`
                                : 'version not reported'}
                            </span>
                          </span>
                          {d.kind === 'laptop' &&
                            (belowMinimum(d, page.minSyncVersion) ||
                              belowRecommended(d, page.minSyncVersion, page.recommendedVersion)) && (
                              <SyncVersionChip
                                below={belowMinimum(d, page.minSyncVersion)}
                                soon={belowRecommended(d, page.minSyncVersion, page.recommendedVersion)}
                              />
                            )}
                          <Button
                            variant="quiet"
                            aria-label={`Release ${name} of ${s.name}`}
                            onClick={() =>
                              ask({
                                what: { device: d.id },
                                words: releaseWords({ kind: 'device', device: name, person: s.name }),
                                done: `${name} released. ${s.name} can set up a replacement now.`,
                              })
                            }
                          >
                            Release
                          </Button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </td>
              <td className="table__actions">
                <Button
                  variant="quiet"
                  aria-label={`Release the seat of ${s.name}`}
                  onClick={() =>
                    ask({
                      what: { seat: s.id },
                      words: releaseWords({ kind: 'seat', person: s.name }),
                      done: `The seat of ${s.name} is released and free for someone else.`,
                    })
                  }
                >
                  Release seat
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
  return (
    <div className="panel stack">
      <h2 className="heading">Seats and devices</h2>
      {staff.length === 0 ? <p className="body muted">Nobody holds a seat yet.</p> : table(staff)}
      {/* decision 918: pilot-only sign-ins take no seat and are counted apart */}
      <h3 className="subheading">Pilot sign-ins</h3>
      <p className="caption muted" data-testid="pilot-seats">
        {pilotSeatWords(pilots.length, pilotPhones, limits.mobilesPerUser)}
      </p>
      {pilots.length > 0 && table(pilots)}
      {ending && (
        <div className="panel confirm stack" role="dialog" aria-labelledby="release-title">
          <p id="release-title" className="body">
            {ending.words}
          </p>
          <label className="label muted" htmlFor="release-note">
            Note for the audit log (optional)
          </label>
          <input
            spellCheck
            id="release-note"
            className="input"
            value={note}
            maxLength={300}
            onChange={(e) => setNote(e.target.value)}
          />
          <div className="row">
            <Button
              className="btn--danger"
              onClick={() => {
                const e = ending;
                setEnding(null);
                void onRelease(e.what, note, e.done);
              }}
            >
              Release
            </Button>
            <Button variant="quiet" onClick={() => setEnding(null)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
      <p className="caption muted">
        A released laptop becomes read only at its next start, once it has checked in. A lost or stolen
        laptop: release it here and tell Braynewave, who can revoke it.
      </p>
    </div>
  );
}

function InvoicesPanel({ invoices }: { readonly invoices: readonly Invoice[] }): ReactElement {
  const owing = invoicesOwing(invoices);
  return (
    <div className="panel stack">
      <h2 className="heading">Invoices</h2>
      <p className="body">
        Not paid yet: {owing.length === 0 ? 'nothing' : owing.map(([c, a]) => moneyText(a, c)).join(', ')}.
      </p>
      {invoices.length === 0 ? (
        <p className="body muted">Braynewave has not recorded an invoice yet.</p>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th className="label">Invoice</th>
                <th className="label">Issued</th>
                <th className="label">Period</th>
                <th className="label">Amount</th>
                <th className="label">Status</th>
                <th className="label">Notes</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((i) => (
                <tr key={i.id}>
                  <td className="data">{i.ref}</td>
                  <td>{dateText(i.issuedOn)}</td>
                  <td>
                    {i.periodFrom || i.periodTo
                      ? `${dateText(i.periodFrom)} to ${dateText(i.periodTo)}`
                      : '–'}
                  </td>
                  <td className="data">{moneyText(i.amount, i.currency)}</td>
                  <td>
                    {i.status === 'paid' ? (
                      <span className="chip chip--pass label">Paid {dateText(i.paidOn)}</span>
                    ) : i.status === 'issued' ? (
                      <span className="chip chip--warn label">{INVOICE_STATUS.issued}</span>
                    ) : (
                      <span className="label muted">{INVOICE_STATUS.void}</span>
                    )}
                  </td>
                  <td className="caption">{i.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="caption muted">
        Braynewave sends each invoice by email; this is its record of what was invoiced and paid. Ask
        Braynewave about an invoice.
      </p>
    </div>
  );
}
